import type { AccountTree, UpdateAccountTree } from "@/types/accountTree/accountTree";
import type { UpdateAccount } from "@/types/account/account";
import type { UpdateInsurance } from "@/types/insurance/insurance";
import type {
	CreateParameterFormData,
	Parameter,
} from "@/types/parameter/parameter";

/**
 * Maps a GET `/v1/admin/account-tree/:id` payload into the PUT update shape.
 * Group rows use `{ id, name }` only, matching successful saves from the
 * main-account editor.
 */
export const accountTreeToUpdatePayload = (tree: AccountTree): UpdateAccountTree => ({
	main_account: {
		main_account_id: tree.main_account.main_account_id,
		display_name: tree.main_account.display_name,
		org_number: tree.main_account.org_number,
	},
	accounts: tree.accounts.map(
		(account): UpdateAccount => ({
			id: account.id,
			display_name: account.display_name,
			org_number: account.org_number,
			contact_name: account.contact_name,
			phone_nr: account.phone_nr,
			email: account.email,
			groups: account.groups.map((g) => ({
				id: g.id,
				name: g.name,
			})),
		}),
	),
	insurances: tree.insurances.map(
		(ins): UpdateInsurance => ({
			id: ins.id,
			type: ins.type,
			provider: ins.provider,
			params: (ins.params ?? []) as Parameter[],
			groups: ins.groups,
		}),
	),
});

/**
 * Same as {@link accountTreeToUpdatePayload}, but replaces `params` for any
 * insurance id present in `mergedParamsByInsuranceId` (e.g. after catalog sync).
 */
export const accountTreeWithMergedInsuranceParams = (
	tree: AccountTree,
	mergedParamsByInsuranceId: ReadonlyMap<string, CreateParameterFormData[]>,
): UpdateAccountTree => {
	const base = accountTreeToUpdatePayload(tree);
	return {
		...base,
		insurances: base.insurances.map((row) => {
			const ins = row as UpdateInsurance;
			const merged = mergedParamsByInsuranceId.get(ins.id);
			if (!merged) return row;
			return {
				...ins,
				params: merged as unknown as Parameter[],
			};
		}),
	};
};
